import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import CreateWorkspaceDialog from "@/components/CreateWorkspaceDialog";
import OnboardingNudge from "@/components/OnboardingNudge";
import UsageNudge from "@/components/UsageNudge";
import TrialBanner from "@/components/TrialBanner";
import DunningBanner from "@/components/DunningBanner";
import SiteFooter from "@/components/SiteFooter";
import StrategyConsultantPanel from "@/components/StrategyConsultantPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  LayoutDashboard, Wand2, CalendarDays, Settings as SettingsIcon,
  CreditCard, Zap, ChevronDown, Plus, LogOut, Check, Building2, BarChart3, Shield, Rocket, Lock, Gift, Send, Activity as ActivityIcon, Brain, Menu, Sparkles, Search as SearchIcon,
  Newspaper,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-item-dashboard" },
  { to: "/campaign", label: "1-Click Campaign", icon: Sparkles, testid: "nav-item-campaign" },
  { to: "/keywords", label: "Keyword Research", icon: SearchIcon, testid: "nav-item-keywords" },
  { to: "/content", label: "Blogs & Ads", icon: Newspaper, testid: "nav-item-content" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-item-calendar" },
  { to: "/activity", label: "Activity", icon: ActivityIcon, testid: "nav-item-activity" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-item-analytics" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-item-settings" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-item-billing" },
  { to: "/affiliate", label: "Affiliate", icon: Gift, testid: "nav-item-affiliate" },
];

const NICHE_COLORS = {
  "E-commerce": "#EC4899", "Gaming": "#8B5CF6", "Local Services": "#10B981",
  "Real Estate": "#F59E0B", "Digital Products": "#3B82F6",
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const { workspaces, active, selectWorkspace, refresh } = useWorkspace();
  const [createOpen, setCreateOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [consultantOpen, setConsultantOpen] = useState(false);
  const [consultantSeen, setConsultantSeen] = useState(() => {
    try { return !!localStorage.getItem("ags_consultant_seen"); } catch (e) { return true; }
  });
  const [annualDismissed, setAnnualDismissed] = useState(() => {
    try { return localStorage.getItem("ags_annual_nudge") === "1"; } catch (e) { return false; }
  });
  const dismissAnnual = () => { try { localStorage.setItem("ags_annual_nudge", "1"); } catch (e) {} setAnnualDismissed(true); };
  const openConsultant = () => {
    setConsultantOpen(true);
    if (!consultantSeen) { try { localStorage.setItem("ags_consultant_seen", "1"); } catch (e) {} setConsultantSeen(true); }
  };
  const navigate = useNavigate();
  const location = useLocation();

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();
  const isActivePlan = ["creator", "growth", "agency", "pro"].includes(user?.plan);
  const planLabel = user?.is_legacy_pro ? "CREATOR · LEGACY" : (user?.plan === "growth" ? "GROWTH" : (user?.plan || "").toUpperCase());
  const freeLeft = user?.free_previews_left;
  const hasFreeCredits = typeof freeLeft === "number" && freeLeft > 0;
  const previewOk = location.pathname === "/campaign";
  const paywalled = user && !user.is_admin && !isActivePlan && !hasFreeCredits && !previewOk;
  const onBilling = ["/billing", "/affiliate"].includes(location.pathname);
  const showAnnualNudge = isActivePlan && !user?.is_legacy_pro
    && (user?.billing_interval || "monthly") === "monthly"
    && ["creator", "growth", "agency"].includes(user?.plan)
    && user?.has_campaign
    && !annualDismissed && !onBilling;
  const skipWorkspaceGate = onBilling || location.pathname.startsWith("/admin");
  const navItems = user?.is_admin
    ? [...NAV,
       { to: "/admin", label: "Admin", icon: Shield, testid: "nav-item-admin" },
       { to: "/admin/growth", label: "Growth", icon: Rocket, testid: "nav-item-growth" }]
    : NAV;

  return (
    <div className="min-h-screen bg-background grid-noise">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 border-r border-border bg-card/60 backdrop-blur-xl hidden lg:flex flex-col z-30">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <img src="/brand-icon.png" alt="AutoGridSocial" className="h-8 w-8 rounded-lg" />
          <span className="font-display font-bold tracking-tight">AutoGridSocial</span>
        </div>

        {/* Workspace switcher */}
        <div className="p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="workspace-switcher-dropdown"
                className="w-full flex items-center justify-between rounded-lg border border-border bg-secondary/50 px-3 py-2.5 text-left hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: NICHE_COLORS[active?.niche] || "#6366F1" }} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{active?.name || "No workspace"}</div>
                    <div className="text-xs text-muted-foreground truncate">{active?.brand_name || "Create one"}</div>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start">
              <DropdownMenuLabel className="text-xs">Workspaces</DropdownMenuLabel>
              {workspaces.map((w) => (
                <DropdownMenuItem
                  key={w.id}
                  data-testid={`workspace-option-${w.id}`}
                  onClick={() => selectWorkspace(w.id)}
                  className="cursor-pointer"
                >
                  <span className="h-2 w-2 rounded-full mr-2 shrink-0"
                    style={{ background: NICHE_COLORS[w.niche] || "#6366F1" }} />
                  <span className="truncate flex-1 min-w-0">{w.name}</span>
                  {w.id === active?.id && <Check className="h-4 w-4 text-primary" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="create-workspace-menu-item"
                onClick={() => setCreateOpen(true)}
                className="cursor-pointer text-primary"
              >
                <Plus className="h-4 w-4 mr-2" /> New workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {(() => {
          const byokX = active?.byok_twitter && Object.values(active.byok_twitter).some(Boolean);
          const byokLi = active?.byok_linkedin && Object.values(active.byok_linkedin).some(Boolean);
          const connected = (active?.connected_accounts?.length > 0) || !!active?.publish_webhook_url || byokX || byokLi;
          return (
            <NavLink to="/settings" data-testid="autopublish-status-chip"
              className={`mx-4 mb-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${connected ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15" : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15"}`}>
              <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-amber-400"}`} />
              <span className="flex-1 min-w-0 truncate font-medium">
                {connected ? "Auto-publish: connected" : "Auto-publish: not connected"}
              </span>
              {!connected && <span className="text-[10px] underline shrink-0">Set up</span>}
            </NavLink>
          );
        })()}

        <nav className="flex-1 px-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          <button
            data-testid="nav-item-consultant"
            onClick={openConsultant}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${consultantSeen ? "text-muted-foreground hover:text-foreground hover:bg-secondary/60" : "text-primary bg-primary/10 ring-1 ring-primary/30"}`}
          >
            <span className="relative flex items-center">
              <Brain className="h-4 w-4" />
              {!consultantSeen && (
                <span className="absolute -top-1.5 -right-1.5 flex h-2.5 w-2.5" data-testid="consultant-spotlight">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                </span>
              )}
            </span>
            AI Consultant
            <Badge className="ml-auto bg-primary/15 text-primary border-primary/30 text-[9px] px-1.5 py-0 h-4">{consultantSeen ? "AI" : "NEW"}</Badge>
          </button>
        </nav>

        <OnboardingNudge />

        <div className="p-4 border-t border-border flex items-center gap-2">
          <div className="flex-1 min-w-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-secondary/60 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 text-left flex-1">
                  <div className="text-sm font-medium truncate">{user?.name || "User"}</div>
                  <Badge variant="outline" className="text-[10px] py-0 h-4 border-primary/40 text-primary">
                    {isActivePlan
                      ? `${planLabel}${user?.in_paid_trial ? ` · TRIAL ${user?.trial_days_left ?? 0}d` : ""}`
                      : hasFreeCredits ? `FREE · ${freeLeft} left` : "NO PLAN"}
                  </Badge>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/billing")} className="cursor-pointer">
                <CreditCard className="h-4 w-4 mr-2" /> Billing
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="cursor-pointer text-rose-400">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
          <NotificationBell />
        </div>
      </aside>

      {/* Main */}
      <main className="lg:pl-64">
        {/* Mobile top bar */}
        <div className="lg:hidden h-16 border-b border-border flex items-center justify-between px-4 bg-card/60 backdrop-blur-xl sticky top-0 z-20">
          <div className="flex items-center gap-2 min-w-0">
            <Zap className="h-5 w-5 text-primary shrink-0" />
            <span className="font-display font-bold truncate">AutoGridSocial</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <NotificationBell />
            <button data-testid="nav-item-consultant-mobile" onClick={openConsultant} className="relative p-2 rounded-md text-primary" aria-label="AI Consultant">
              <Brain className="h-5 w-5" />
              {!consultantSeen && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary animate-ping" />}
            </button>
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" aria-label="Open navigation menu" className="p-2 rounded-md text-foreground hover:bg-secondary/60 transition-colors">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] max-w-[85vw] p-0 flex flex-col bg-card/95 backdrop-blur-xl">
                <SheetHeader className="h-16 flex-row items-center gap-2 px-5 border-b border-border space-y-0 text-left">
                  <img src="/brand-icon.png" alt="AutoGridSocial" className="h-8 w-8 rounded-lg" />
                  <SheetTitle className="font-display font-bold tracking-tight">AutoGridSocial</SheetTitle>
                  <SheetDescription className="sr-only">Navigation menu</SheetDescription>
                </SheetHeader>

                {/* Workspace switcher */}
                <div className="p-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid="workspace-switcher-dropdown-mobile" className="w-full flex items-center justify-between rounded-lg border border-border bg-secondary/50 px-3 py-2.5 text-left">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: NICHE_COLORS[active?.niche] || "#6366F1" }} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{active?.name || "No workspace"}</div>
                            <div className="text-xs text-muted-foreground truncate">{active?.brand_name || "Create one"}</div>
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[260px]" align="start">
                      <DropdownMenuLabel className="text-xs">Workspaces</DropdownMenuLabel>
                      {workspaces.map((w) => (
                        <DropdownMenuItem key={w.id} onClick={() => selectWorkspace(w.id)} className="cursor-pointer">
                          <span className="h-2 w-2 rounded-full mr-2 shrink-0" style={{ background: NICHE_COLORS[w.niche] || "#6366F1" }} />
                          <span className="truncate flex-1 min-w-0">{w.name}</span>
                          {w.id === active?.id && <Check className="h-4 w-4 text-primary" />}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { setMobileNavOpen(false); setCreateOpen(true); }} className="cursor-pointer text-primary">
                        <Plus className="h-4 w-4 mr-2" /> New workspace
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 space-y-1 pb-2">
                  {navItems.map((item) => (
                    <NavLink key={item.to} to={item.to} data-testid={`${item.testid}-mobile`} onClick={() => setMobileNavOpen(false)}
                      className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}>
                      <item.icon className="h-5 w-5 shrink-0" /> {item.label}
                    </NavLink>
                  ))}
                  <button data-testid="nav-item-consultant-menu" onClick={() => { setMobileNavOpen(false); openConsultant(); }}
                    className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors">
                    <Brain className="h-5 w-5 shrink-0" /> AI Consultant
                    <Badge className="ml-auto bg-primary/15 text-primary border-primary/30 text-[9px] px-1.5 py-0 h-4">{consultantSeen ? "AI" : "NEW"}</Badge>
                  </button>
                </nav>

                <div className="p-4 border-t border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{user?.name || "User"}</div>
                      <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="text-[10px] py-0 h-5 border-primary/40 text-primary">
                      {isActivePlan
                        ? `${planLabel}${user?.in_paid_trial ? ` · TRIAL ${user?.trial_days_left ?? 0}d` : ""}`
                        : hasFreeCredits ? `FREE · ${freeLeft} left` : "NO PLAN"}
                    </Badge>
                    <Button variant="ghost" size="sm" data-testid="logout-button-mobile" onClick={logout} className="text-rose-400 hover:text-rose-400 hover:bg-rose-500/10">
                      <LogOut className="h-4 w-4 mr-1.5" /> Log out
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {paywalled && !onBilling ? (
            <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="paywall-gate">
              <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center mb-6">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-display font-bold mb-2">Unlock AutoGridSocial</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                Plans start at <span className="text-foreground font-medium">$29/mo</span> for unlimited 1-click campaigns — add Shopify sync and the AI Consultant on E-commerce Growth. Cancel anytime in one click.
              </p>
              <Button data-testid="paywall-cta" onClick={() => navigate("/billing")} className="bg-primary hover:bg-primary/90 text-white">
                <Rocket className="h-4 w-4 mr-2" /> See plans
              </Button>
            </div>
          ) : workspaces.length === 0 && !skipWorkspaceGate ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center mb-6">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-display font-bold mb-2">Create your first workspace</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                A workspace holds one brand's niche, tone, assets and connected social accounts.
              </p>
              <Button data-testid="create-workspace-button" onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
                <Plus className="h-4 w-4 mr-2" /> New workspace
              </Button>
            </div>
          ) : (
            <>
              {hasFreeCredits && (
                <div className={`mb-6 rounded-xl border px-5 py-3 flex items-center justify-between gap-3 flex-wrap ${freeLeft === 1 ? "border-amber-500/50 bg-amber-500/10" : "border-primary/30 bg-primary/5"}`} data-testid="free-tier-banner">
                  <p className="text-sm text-foreground/90">
                    {freeLeft === 1 ? (
                      <><span className="font-semibold text-amber-400">Last free campaign preview!</span> Choose a plan (from $29/mo) to unlock all 30 posts &amp; auto-scheduling.</>
                    ) : (
                      <>You're on the free tier — <span className="font-semibold text-primary">{freeLeft} free campaign previews</span> left (3 posts each). Choose a plan to unlock all 30 &amp; schedule.</>
                    )}
                  </p>
                  <Button size="sm" data-testid="free-tier-upgrade" onClick={() => navigate("/billing")} className={`shrink-0 text-white ${freeLeft === 1 ? "bg-amber-500 hover:bg-amber-500/90" : "bg-primary hover:bg-primary/90"}`}>
                    <Rocket className="h-3.5 w-3.5 mr-1.5" /> See plans
                  </Button>
                </div>
              )}
              {showAnnualNudge && (
                <div className="mb-6 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/12 to-teal-500/8 px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="annual-nudge-banner">
                  <p className="text-sm text-foreground/90 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>Save <span className="font-semibold text-emerald-300">2 months</span> — switch to annual billing.</span>
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" data-testid="annual-nudge-cta" onClick={() => navigate("/billing")} className="bg-emerald-600 hover:bg-emerald-600/90 text-white">
                      Switch &amp; save
                    </Button>
                    <Button size="sm" variant="ghost" data-testid="annual-nudge-dismiss" onClick={dismissAnnual} className="text-muted-foreground">Later</Button>
                  </div>
                </div>
              )}
              <UsageNudge />
              <DunningBanner />
              <TrialBanner />
              <Outlet />
            </>
          )}
        </div>
        <SiteFooter />
      </main>

      <CreateWorkspaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async (id) => { await refresh(); selectWorkspace(id); }}
      />

      {active && (
        <StrategyConsultantPanel open={consultantOpen} onOpenChange={setConsultantOpen} />
      )}
    </div>
  );
}
